import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { formatMYR, formatDate } from "./api";

/**
 * Render an invoice as HTML off-screen, snapshot to canvas, embed into PDF.
 * Using html2canvas avoids the Chinese-font-embedding problem in jsPDF.
 */
export async function downloadInvoicePDF(sale, owner, options = {}) {
    const footerNote = (options.footerNote || "").trim();
    // Preload logo as data URL to guarantee it's embedded in the html2canvas snapshot.
    let logoDataUrl = "";
    try {
        const res = await fetch("/logo.jpg", { cache: "force-cache" });
        const blob = await res.blob();
        logoDataUrl = await new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result);
            fr.onerror = reject;
            fr.readAsDataURL(blob);
        });
    } catch {
        logoDataUrl = "";
    }

    // A4 aspect ratio: 210 x 297 mm → 800 x ~1131 px. Force minHeight so content fills the page.
    const node = document.createElement("div");
    node.style.position = "fixed";
    node.style.left = "-10000px";
    node.style.top = "0";
    node.style.width = "800px";
    node.style.minHeight = "1131px";
    node.style.padding = "40px 44px";
    node.style.background = "white";
    node.style.fontFamily = '"Noto Sans SC", -apple-system, sans-serif';
    node.style.color = "#0F172A";
    node.style.display = "flex";
    node.style.flexDirection = "column";

    const itemsHtml = sale.items
        .map(
            (it, i) => `
        <tr>
            <td style="padding:10px 8px; border-bottom:1px solid #E2E8F0; font-size:13px;">${i + 1}</td>
            <td style="padding:10px 8px; border-bottom:1px solid #E2E8F0; font-size:13px;">${escapeHtml(it.product_name)}</td>
            <td style="padding:10px 8px; border-bottom:1px solid #E2E8F0; font-size:13px; font-family:'JetBrains Mono',monospace; color:#64748B;">${escapeHtml(it.sku)}</td>
            <td style="padding:10px 8px; border-bottom:1px solid #E2E8F0; font-size:13px; text-align:right; font-family:'JetBrains Mono',monospace;">${it.quantity}</td>
            <td style="padding:10px 8px; border-bottom:1px solid #E2E8F0; font-size:13px; text-align:right; font-family:'JetBrains Mono',monospace;">${formatMYR(it.unit_price)}</td>
            <td style="padding:10px 8px; border-bottom:1px solid #E2E8F0; font-size:13px; text-align:right; font-family:'JetBrains Mono',monospace; font-weight:600;">${formatMYR(it.line_total)}</td>
        </tr>
    `,
        )
        .join("");

    const hasDelivery = sale.delivery_method || sale.delivery_address || sale.delivery_contact || sale.tracking_no;

    node.innerHTML = `
        <div style="flex:0 0 auto;">
            <div style="display:flex; justify-content:space-between; align-items:flex-start; border-bottom: 3px solid #B08A3E; padding-bottom:22px;">
                <div style="display:flex; align-items:center; gap:18px;">
                    ${logoDataUrl ? `<img src="${logoDataUrl}" alt="FuYuan" style="width:88px; height:88px; object-fit:contain;" />` : ""}
                    <div>
                        <div style="font-size:12px; letter-spacing:2px; text-transform:uppercase; color:#64748B;">FuYuan Jewelry</div>
                        <div style="font-size:28px; font-weight:700; margin-top:6px;">福缘珠宝</div>
                        <div style="font-size:11px; color:#94A3B8; margin-top:6px;">Fine Jewelry · 精品珠宝</div>
                    </div>
                </div>
                <div style="text-align:right;">
                    <div style="font-size:12px; letter-spacing:2px; text-transform:uppercase; color:#64748B;">Invoice · 销售发票</div>
                    <div style="font-size:24px; font-weight:800; font-family:'JetBrains Mono',monospace; margin-top:6px;">${escapeHtml(sale.invoice_no)}</div>
                    <div style="font-size:13px; color:#64748B; margin-top:8px; font-family:'JetBrains Mono',monospace;">${escapeHtml(formatDate(sale.created_at))}</div>
                </div>
            </div>

            <div style="display:flex; justify-content:space-between; margin-top:28px; padding:20px; background:#F8FAFC; border-radius:8px;">
                <div>
                    <div style="font-size:11px; letter-spacing:1.5px; text-transform:uppercase; color:#64748B;">客户 Customer</div>
                    <div style="font-size:18px; font-weight:600; margin-top:6px;">${escapeHtml(sale.customer_name || "散客")}</div>
                    ${sale.customer_phone ? `<div style="font-size:12px; color:#64748B; margin-top:4px; font-family:'JetBrains Mono',monospace;">${escapeHtml(sale.customer_phone)}</div>` : ""}
                </div>
                <div style="text-align:right;">
                    <div style="font-size:11px; letter-spacing:1.5px; text-transform:uppercase; color:#64748B;">应付总额 Total Due</div>
                    <div style="font-size:26px; font-weight:800; color:#B08A3E; font-family:'JetBrains Mono',monospace; margin-top:6px;">${formatMYR(sale.total)}</div>
                </div>
            </div>

            <table style="width:100%; margin-top:30px; border-collapse:collapse;">
                <thead>
                    <tr style="border-bottom:2px solid #0F172A;">
                        <th style="padding:12px 10px; text-align:left; font-size:11px; letter-spacing:1px; text-transform:uppercase; color:#64748B; width:36px;">#</th>
                        <th style="padding:12px 10px; text-align:left; font-size:11px; letter-spacing:1px; text-transform:uppercase; color:#64748B;">产品 Item</th>
                        <th style="padding:12px 10px; text-align:left; font-size:11px; letter-spacing:1px; text-transform:uppercase; color:#64748B;">SKU</th>
                        <th style="padding:12px 10px; text-align:right; font-size:11px; letter-spacing:1px; text-transform:uppercase; color:#64748B;">数量 Qty</th>
                        <th style="padding:12px 10px; text-align:right; font-size:11px; letter-spacing:1px; text-transform:uppercase; color:#64748B;">单价 Price</th>
                        <th style="padding:12px 10px; text-align:right; font-size:11px; letter-spacing:1px; text-transform:uppercase; color:#64748B;">小计 Subtotal</th>
                    </tr>
                </thead>
                <tbody>${itemsHtml}</tbody>
            </table>

            <div style="display:flex; justify-content:flex-end; margin-top:28px;">
                <div style="min-width:320px;">
                    <div style="display:flex; justify-content:space-between; padding:8px 0; font-size:14px;">
                        <span style="color:#64748B;">商品小计 Subtotal</span>
                        <span style="font-family:'JetBrains Mono',monospace;">${formatMYR(sale.subtotal)}</span>
                    </div>
                    <div style="display:flex; justify-content:space-between; padding:8px 0; font-size:14px;">
                        <span style="color:#64748B;">折扣 Discount</span>
                        <span style="font-family:'JetBrains Mono',monospace; color:#DC2626;">- ${formatMYR(sale.discount)}</span>
                    </div>
                    <div style="display:flex; justify-content:space-between; padding:14px 0; border-top:2px solid #0F172A; margin-top:6px;">
                        <span style="font-size:13px; letter-spacing:1.5px; text-transform:uppercase; color:#0F172A; font-weight:600;">合计 Total</span>
                        <span style="font-family:'JetBrains Mono',monospace; font-size:22px; font-weight:800; color:#B08A3E;">${formatMYR(sale.total)}</span>
                    </div>
                </div>
            </div>

            ${hasDelivery ? `
            <div style="margin-top:26px; padding:14px 18px; background:#EFF6FF; border-radius:8px; border-left:3px solid #2563EB;">
                <div style="font-size:11px; letter-spacing:1.5px; text-transform:uppercase; color:#1E3A8A; font-weight:600;">配送信息 Delivery</div>
                <div style="margin-top:8px; display:grid; grid-template-columns:1fr 1fr; gap:8px 20px; font-size:12px; color:#334155;">
                    ${sale.delivery_method ? `<div>方式：<span style="color:#0F172A; font-weight:600;">${escapeHtml(sale.delivery_method)}</span></div>` : ""}
                    ${sale.tracking_no ? `<div>快递单号：<span style="color:#0F172A; font-weight:600; font-family:'JetBrains Mono',monospace;">${escapeHtml(sale.tracking_no)}</span></div>` : ""}
                    ${sale.delivery_contact ? `<div>联系人：<span style="color:#0F172A; font-weight:600;">${escapeHtml(sale.delivery_contact)}</span></div>` : ""}
                    ${sale.delivery_address ? `<div style="grid-column:1 / -1;">地址：<span style="color:#0F172A; font-weight:600;">${escapeHtml(sale.delivery_address)}</span></div>` : ""}
                </div>
            </div>` : ""}

            ${(sale.platform || sale.payment_method) ? `
            <div style="margin-top:20px; padding:14px 18px; background:#F8FAFC; border-radius:8px; display:flex; gap:36px; font-size:13px;">
                ${sale.platform ? `<div><span style="color:#64748B; text-transform:uppercase; letter-spacing:1px; font-size:10px;">销售平台 Platform</span><div style="margin-top:4px; font-weight:600; color:#0F172A;">${escapeHtml(sale.platform)}</div></div>` : ""}
                ${sale.payment_method ? `<div><span style="color:#64748B; text-transform:uppercase; letter-spacing:1px; font-size:10px;">付款方式 Payment</span><div style="margin-top:4px; font-weight:600; color:#0F172A;">${escapeHtml(sale.payment_method)}</div></div>` : ""}
            </div>` : ""}

            ${sale.note ? `
            <div style="margin-top:22px; padding-top:14px; border-top:1px dashed #CBD5E1;">
                <div style="font-size:11px; letter-spacing:1px; text-transform:uppercase; color:#64748B;">备注 Note</div>
                <div style="font-size:13px; margin-top:6px; color:#475569; line-height:1.6;">${escapeHtml(sale.note)}</div>
            </div>` : ""}
        </div>

        <div style="flex:1 1 auto;"></div>

        <div style="flex:0 0 auto;">
            <div style="margin-top:28px; padding:16px 20px; background:#FBF7EF; border-radius:8px; border:1px solid #E7D9B6;">
                <div style="font-size:11px; letter-spacing:1.5px; text-transform:uppercase; color:#8A6D2F; font-weight:600;">条款 Terms</div>
                <div style="font-size:11px; color:#5A4520; margin-top:6px; line-height:1.7;">
                    • 所有售出商品经检查确认后不予退换 / All sold items are final and non-refundable.<br/>
                    • 请在收货 7 日内验收 / Please verify goods within 7 days of receipt.<br/>
                    • 保留发票以便日后服务 / Please keep this invoice for future service.
                </div>
            </div>
            <div style="margin-top:20px; padding-top:16px; border-top:2px solid #B08A3E; display:flex; justify-content:space-between; align-items:center; font-size:11px; color:#94A3B8;">
                <span style="color:#0F172A; font-weight:600;">${footerNote ? escapeHtml(footerNote) : "感谢惠顾 · Thank you for your purchase"}</span>
                <span style="font-family:'JetBrains Mono',monospace;">FuYuan Jewelry · 福缘珠宝</span>
            </div>
        </div>
    `;

    document.body.appendChild(node);
    try {
        // Wait a tick for fonts to settle
        await document.fonts?.ready?.catch(() => {});
        const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
        const imgData = canvas.toDataURL("image/jpeg", 0.92);

        const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();
        const imgW = pageW - 20;
        const imgH = (canvas.height * imgW) / canvas.width;

        if (imgH <= pageH - 20) {
            pdf.addImage(imgData, "JPEG", 10, 10, imgW, imgH);
        } else {
            // Multi-page
            const ratio = imgW / canvas.width;
            const pageCanvasH = (pageH - 20) / ratio;
            let y = 0;
            while (y < canvas.height) {
                const sliceCanvas = document.createElement("canvas");
                sliceCanvas.width = canvas.width;
                sliceCanvas.height = Math.min(pageCanvasH, canvas.height - y);
                sliceCanvas.getContext("2d").drawImage(canvas, 0, -y);
                const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.92);
                if (y > 0) pdf.addPage();
                pdf.addImage(sliceData, "JPEG", 10, 10, imgW, sliceCanvas.height * ratio);
                y += pageCanvasH;
            }
        }
        pdf.save(`${sale.invoice_no}.pdf`);
    } finally {
        node.remove();
    }
}

function escapeHtml(s) {
    return String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}
